import { OrganizationService } from "../services/organization.ts";
import { injectAesthetic } from "./library/ui/shared-styles.ts";
import './library/ui/app-icon.ts';

declare var Stripe: any;

export class AppPaymentForm extends HTMLElement {
    private seed: number;
    private stripe: any;
    private cardElement: any;
    private clientSecret: string | null = null;
    private orgId: string | null = null;
    private isSubmitting: boolean = false;
    private error: string | null = null;

    private isStripeReady: boolean = false;

    constructor() {
        super();
        this.seed = Math.floor(Math.random() * 10000);
    }

    static get observedAttributes() {
        return ['open', 'org-id'];
    }

    connectedCallback() {
        this.render();
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        if (oldValue === newValue) return;

        if (name === 'open' && newValue !== null) {
            this.orgId = this.getAttribute('org-id');
            this.isSubmitting = true;
            this.error = null;
            this.isStripeReady = false;
            this.render();
            this.initializePayment();
        } else if (name === 'org-id') {
            this.orgId = newValue;
        }
    }

    private async initializePayment() {
        if (!this.orgId) return;

        try {
            const res = await OrganizationService.getInstance().setupBilling(this.orgId, '');
            
            // Only update secret if it changed to avoid unnecessary re-mounts
            if (this.clientSecret !== res.client_secret) {
                this.clientSecret = res.client_secret;
                this.isStripeReady = false;
            }
            
            if (!this.stripe) {
                this.stripe = Stripe(res.stripe_pk);
            }

            this.mountStripe();
        } catch (e: any) {
            console.error('Failed to initialize payment', e);
            this.error = e.message || 'Failed to initialize payment system.';
        } finally {
            this.isSubmitting = false;
            this.render();
        }
    }

    private mountStripe() {
        if (!this.stripe || !this.clientSecret) return;

        // If already mounted and ready, don't do anything
        if (this.cardElement && this.isStripeReady) return;

        // If we have an old element, destroy it first
        if (this.cardElement) {
            this.cardElement.unmount();
            this.cardElement.destroy();
            this.cardElement = null;
        }

        const elements = this.stripe.elements();
        this.cardElement = elements.create('card', {
            style: {
                base: {
                    color: '#ffffff',
                    fontFamily: 'Outfit, sans-serif',
                    fontSmoothing: 'antialiased',
                    fontSize: '16px',
                    '::placeholder': { color: '#94a3b8' }
                },
                invalid: { color: '#ef4444', iconColor: '#ef4444' }
            }
        });

        this.cardElement.on('ready', () => {
            this.isStripeReady = true;
        });

        this.cardElement.on('change', (event: any) => {
            if (event.error) {
                this.error = event.error.message;
                this.render();
            } else {
                this.error = null;
                this.render();
            }
        });

        // Ensure the DOM is ready for mounting
        setTimeout(() => {
            const mountPoint = this.querySelector('#card-element');
            if (mountPoint) {
                this.cardElement.mount(mountPoint);
            }
        }, 0);
    }

    private async handleSubmit() {
        if (this.isSubmitting || !this.stripe || !this.clientSecret || !this.orgId || !this.cardElement) return;
        
        if (!this.isStripeReady) {
            this.error = "Payment system is still loading. Please wait a moment.";
            this.render();
            return;
        }

        this.isSubmitting = true;
        this.error = null;
        this.render();

        try {
            const result = await this.stripe.confirmCardSetup(this.clientSecret, {
                payment_method: {
                    card: this.cardElement,
                },
            });

            if (result.error) {
                throw new Error(result.error.message);
            }

            await OrganizationService.getInstance().confirmPayment(this.orgId, result.setupIntent.id);

            this.dispatchEvent(new CustomEvent('payment-updated', { 
                bubbles: true, 
                composed: true,
                detail: { success: true }
            }));
            
            this.close();
        } catch (e: any) {
            console.error('Payment update failed', e);
            this.error = e.message || 'Failed to update payment method.';
            this.isSubmitting = false;
            this.render();
        }
    }

    public async submit() {
        return this.handleSubmit();
    }

    public get loading() {
        return this.isSubmitting;
    }

    private close() {
        this.removeAttribute('open');
        this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }));
    }

    private render() {
        // Initialize structure once
        if (!this.querySelector('.payment-form')) {
            this.innerHTML = `
                ${injectAesthetic(this.seed)}
                <style>
                    app-payment-form { display: block; }
                    
                    .stripe-container {
                        padding: 0;
                    }

                    #card-element {
                        background: rgba(255, 255, 255, 0.05);
                        padding: 16px;
                        border-radius: 8px;
                        border: 1px solid rgba(255, 255, 255, 0.1);
                        margin-bottom: 8px;
                        transition: border-color 0.2s;
                        min-height: 50px;
                    }

                    #card-element.StripeElement--focus {
                        border-color: var(--accent);
                    }

                    .error-message {
                        color: #ef4444;
                        font-size: 14px;
                        margin-top: 8px;
                        display: flex;
                        align-items: center;
                        gap: 8px;
                    }

                    .loading-spinner {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        padding: 3rem 0;
                        gap: 1rem;
                        color: var(--text-dim);
                        font-family: var(--font-mono);
                    }

                    .spinner {
                        width: 32px;
                        height: 32px;
                        border: 3px solid rgba(245, 158, 11, 0.1);
                        border-top: 3px solid var(--accent);
                        border-radius: 50%;
                        animation: spin 1s linear infinite;
                    }

                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }

                    .info-text {
                        color: var(--text-dim);
                        font-size: 14px;
                        margin-bottom: 24px;
                        line-height: 1.5;
                    }
                </style>

                <div class="payment-form">
                    <div class="loading-spinner" id="form-loader">
                        <div class="spinner"></div>
                        <span>Initializing secure session...</span>
                    </div>
                    
                    <div id="form-content" style="display: none;">
                        <p class="info-text">
                            Enter your new card details below. Your card will be securely stored with Stripe and used for future subscription payments.
                        </p>
                        
                        <div class="stripe-container">
                            <div id="card-element"></div>
                            <div class="error-message" id="error-container" style="display: none;"></div>
                        </div>
                    </div>
                </div>
            `;
        }

        const loader = this.querySelector('#form-loader') as HTMLElement;
        const content = this.querySelector('#form-content') as HTMLElement;
        const errorContainer = this.querySelector('#error-container') as HTMLElement;

        if (!loader || !content || !errorContainer) return;

        if (this.isSubmitting && !this.clientSecret) {
            loader.style.display = 'flex';
            content.style.display = 'none';
        } else {
            loader.style.display = 'none';
            content.style.display = 'block';
        }

        if (this.error) {
            errorContainer.style.display = 'flex';
            errorContainer.innerHTML = `<span>⚠️</span> ${this.error}`;
        } else {
            errorContainer.style.display = 'none';
        }
    }
}

customElements.define('app-payment-form', AppPaymentForm);
